// src/pages/workflow/CreateWorkflow.tsx
import { type ProductGroup, useGetProviders, useSaveProductGroup } from '@/hooks/useProductGroups';
import { useEffect, useRef, useState } from 'react';

import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

interface Vendor {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  providerId: number; // Added to map back to provider
  vendorCode: string; // Added for API
  productCode: string; // Added for API
}

interface CreateWorkflowProps {
  onClose: () => void;
  onSuccess?: () => void;
  initialData?: ProductGroup; // Pass the full ProductGroup for editing
  mode?: 'create' | 'edit';
  providerTag?: string; // Tag used to fetch providers (defaults to 'AVM')
}

export default function CreateWorkflow({
  onClose,
  onSuccess,
  initialData,
  mode = 'create',
  providerTag = 'AVM',
}: CreateWorkflowProps) {
  const [workflowName, setWorkflowName] = useState(initialData?.name ?? '');
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const dragIndex = useRef<number | null>(null);

  // Fetch providers from API (only for create mode)
  const { data: providers, isLoading: isLoadingProviders } = useGetProviders(
    mode === 'create' ? providerTag : ''
  );

  // Save mutation
  const saveProductGroupMutation = useSaveProductGroup();

  // Initialize vendors from API data when available (only for create mode)
  useEffect(() => {
    if (mode === 'create' && providers && providers.length > 0 && vendors.length === 0) {
      const transformedVendors: Vendor[] = providers.map((provider) => ({
        id: provider.id.toString(),
        name: provider.productName,
        description: provider.vendorName ?? provider.vendorCode ?? '',
        enabled: true,
        providerId: provider.id,
        vendorCode: provider.vendorCode,
        productCode: provider.productCode,
      }));
      setVendors(transformedVendors);
    }
  }, [providers, mode, vendors.length]);

  // Initialize vendors from initialData (for edit mode)
  useEffect(() => {
    if (mode === 'edit' && initialData?.products && vendors.length === 0) {
      const transformedVendors: Vendor[] = initialData.products
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((product) => ({
          id: product.providerId.toString(),
          name: product.productName || product.productCode,
          description: product.productCode,
          enabled: product.active,
          providerId: product.providerId,
          vendorCode: product.vendorCode,
          productCode: product.productCode,
        }));
      setVendors(transformedVendors);
    }
  }, [initialData, mode, vendors.length]);

  const moveUp = (index: number) => {
    if (index === 0) return;
    const updated = [...vendors];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    setVendors(updated);
  };

  const moveDown = (index: number) => {
    if (index === vendors.length - 1) return;
    const updated = [...vendors];
    [updated[index + 1], updated[index]] = [updated[index], updated[index + 1]];
    setVendors(updated);
  };

  const toggleEnabled = (id: string) => {
    const vendor = vendors.find((v) => v.id === id);
    if (!vendor) return;

    if (vendor.enabled) {
      const enabledCount = vendors.filter((v) => v.enabled).length;
      if (enabledCount <= 1) {
        setValidationError('At least one vendor must remain enabled.');
        return;
      }
    }

    setValidationError(null);
    setVendors(vendors.map((v) => (v.id === id ? { ...v, enabled: !v.enabled } : v)));
  };

  const handleDragStart = (index: number) => {
    dragIndex.current = index;
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDrop = (index: number) => {
    if (dragIndex.current === null || dragIndex.current === index) {
      setDragOverIndex(null);
      dragIndex.current = null;
      return;
    }
    const updated = [...vendors];
    const [moved] = updated.splice(dragIndex.current, 1);
    updated.splice(index, 0, moved);
    setVendors(updated);
    dragIndex.current = null;
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    dragIndex.current = null;
    setDragOverIndex(null);
  };

  const handleSubmit = () => {
    if (!workflowName.trim()) return;

    // Build the save request payload
    const payload = {
      ...(mode === 'edit' && initialData?.id ? { id: initialData.id } : {}),
      name: workflowName,
      vendorCode: null,
      productCode: null,
      productGroupType: 'CASCADE' as const,
      active: true,
      ...(mode === 'edit' && initialData?.default ? { default: initialData.default } : {}),
      products: vendors.map((vendor, index) => ({
        providerId: vendor.providerId,
        vendorCode: vendor.vendorCode,
        productCode: vendor.productCode,
        sortOrder: index, // Order starts at 0, reflects position in list
        active: vendor.enabled,
      })),
    };

    // Call the save mutation
    saveProductGroupMutation.mutate(payload, {
      onSuccess: () => {
        onSuccess?.();
        onClose();
      },
    });
  };

  const enabledCount = vendors.filter((v) => v.enabled).length;

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{mode === 'edit' ? 'Edit Workflow' : 'Create New Workflow'}</Typography>
        </Stack>
      </DialogTitle>

      <Divider />

      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          {/* Workflow Name */}
          <TextField
            fullWidth
            required
            label="Workflow Name"
            placeholder="Enter workflow name"
            value={workflowName}
            onChange={(e) => setWorkflowName(e.target.value)}
          />

          {/* Loading state */}
          {isLoadingProviders && mode === 'create' && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <Typography color="text.secondary">Loading providers...</Typography>
            </Box>
          )}

          {/* Vendor Sequence header */}
          {vendors.length > 0 && (
            <>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2" color="text.secondary">
                  Vendor Sequence
                </Typography>
                <Chip
                  label={`${enabledCount} of ${vendors.length} enabled`}
                  size="small"
                  variant="outlined"
                  color="default"
                />
              </Stack>

              {/* Validation error */}
              {validationError && (
                <Alert severity="error" onClose={() => setValidationError(null)}>
                  {validationError}
                </Alert>
              )}

              {/* Vendor list */}
              <Stack spacing={1.5}>
            {vendors.map((vendor, index) => (
              <Paper
                key={vendor.id}
                variant="outlined"
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={() => handleDrop(index)}
                onDragEnd={handleDragEnd}
                sx={{
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  opacity: !vendor.enabled ? 0.55 : 1,
                  borderColor: dragOverIndex === index
                    ? 'primary.main'
                    : vendor.enabled
                    ? 'primary.dark'
                    : 'divider',
                  borderStyle: dragOverIndex === index ? 'dashed' : 'solid',
                  transform: dragOverIndex === index ? 'scale(1.01)' : 'scale(1)',
                  transition: 'transform 0.15s ease, border-color 0.15s ease, opacity 0.2s ease',
                  cursor: 'default',
                }}
              >
                {/* Drag handle */}
                <Tooltip title="Drag to reorder">
                  <DragIndicatorIcon
                    fontSize="small"
                    sx={{ color: 'text.disabled', cursor: 'grab', flexShrink: 0 }}
                  />
                </Tooltip>

                {/* Order badge */}
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    bgcolor: 'background.neutral',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {index + 1}
                  </Typography>
                </Box>

                {/* Vendor info */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={{
                      textDecoration: vendor.enabled ? 'none' : 'line-through',
                      color: vendor.enabled ? 'text.primary' : 'text.disabled',
                    }}
                  >
                    {vendor.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {vendor.description}
                  </Typography>
                </Box>

                {/* Disabled chip */}
                {!vendor.enabled && (
                  <Chip label="Disabled" size="small" color="default" variant="outlined" />
                )}

                {/* Up / Down arrows */}
                <Stack direction="column" spacing={0}>
                  <IconButton
                    size="small"
                    onClick={() => moveUp(index)}
                    disabled={index === 0}
                    aria-label="Move up"
                  >
                    <KeyboardArrowUpIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => moveDown(index)}
                    disabled={index === vendors.length - 1}
                    aria-label="Move down"
                  >
                    <KeyboardArrowDownIcon fontSize="small" />
                  </IconButton>
                </Stack>

                {/* Power toggle */}
                <Tooltip title={vendor.enabled ? 'Click to disable' : 'Click to enable'}>
                  <IconButton
                    size="small"
                    onClick={() => toggleEnabled(vendor.id)}
                    aria-label={vendor.enabled ? 'Disable vendor' : 'Enable vendor'}
                    sx={{
                      color: vendor.enabled ? 'primary.main' : 'text.disabled',
                      filter: vendor.enabled ? 'drop-shadow(0 0 4px currentColor)' : 'none',
                      transition: 'color 0.2s ease, filter 0.2s ease',
                    }}
                  >
                    <PowerSettingsNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Paper>
            ))}
          </Stack>
            </>
          )}
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!workflowName.trim() || saveProductGroupMutation.isPending}
          onClick={handleSubmit}
        >
          {saveProductGroupMutation.isPending
            ? 'Saving...'
            : mode === 'edit'
            ? 'Save Changes'
            : 'Create Workflow'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
